import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ReportCommonUsagePanel — §6.8 공통 모듈 시연 패널
 * C082 리포트·C081 바코드·C078 Excel·C079 PDF 재사용 현황 표시
 */
export function ReportCommonUsagePanel() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const usages = [
    {
      module: '★ eraf-report (C082)',
      api: 'ReportService.export(def, format)',
      role: '단일 정의 → EXCEL/PDF 합성 생성 (biz 분기 0줄)',
      snippet: 'reportService.export(commonDef, ReportFormat.from(p))',
    },
    {
      module: '★ eraf-report (C082)',
      api: 'ReportDefinition.builder().column(...).row(...)',
      role: 'biz 영속 정의 → 공통 런타임 정의로 변환',
      snippet: 'ReportDefinition.builder().title(name).column(new ReportColumn(...))',
    },
    {
      module: '★ eraf-report (C082)',
      api: 'ReportFormat.EXCEL/PDF · contentType() · fileExtension()',
      role: '응답 Content-Type/파일명 (분기 없이)',
      snippet: 'MediaType.parseMediaType(format.contentType())',
    },
    {
      module: '★ eraf-core (C081)',
      api: 'BarcodeService.generateQr(text, size, ec)',
      role: '셀에 QR PNG 임베드 (한글 ECI · mojibake 없음)',
      snippet: 'barcodeService.generateQr(assetCode, 96, QrErrorCorrection.H)',
    },
    {
      module: '★ eraf-core (C081)',
      api: 'BarcodeService.generateCode128(text, w, h)',
      role: '셀에 Code128 PNG 임베드',
      snippet: 'barcodeService.generateCode128(sku, 240, 80)',
    },
    {
      module: '★ eraf-core (C081)',
      api: 'BarcodeService.decode(byte[]) → DecodedResult',
      role: '업로드 라벨 판독 (역방향 시연)',
      snippet: 'DecodedResult r = barcodeService.decode(bytes)',
    },
    {
      module: 'eraf-image (C080)',
      api: 'ImageProcessor.thumbnail(byte[], maxDim)',
      role: '임베드 로고 EXIF 제거 리사이즈',
      snippet: 'imageProcessor.thumbnail(logoBytes, 128)',
    },
    {
      module: 'eraf-excel (C078)',
      api: 'ExcelExporter.write(out, rows, opts)',
      role: '생성 이력 자체 export',
      snippet: 'excelExporter.write(out, logs, ExcelExportOptions.defaults())',
    },
    {
      module: 'eraf-core (C002)',
      api: 'ApiResponse.page(Page<ReportListItem>)',
      role: '목록 표준 응답',
      snippet: 'return ApiResponse.page(service.search(cond, p))',
    },
    {
      module: 'eraf-core (C005)',
      api: 'JsonMapper.fromList(columnsJson, ReportColumnDto.class)',
      role: 'columns_json JSONB 역직렬화',
      snippet: 'json.fromList(def.getColumnsJson(), ReportColumnDto.class)',
    },
    {
      module: 'eraf-core (C006)',
      api: 'RequestContext.userId()',
      role: 'owner 강제 + 본인 판별',
      snippet: 'cond.withOwner(ctx.userId())',
    },
    {
      module: 'eraf-core (C014)',
      api: '@Audited(action="REPORT_GENERATED")',
      role: '생성/수정/삭제 감사 (audit-log 가 소비)',
      snippet: '@Audited(action="REPORT_GENERATED", resource="report-builder")',
    },
    {
      module: 'eraf-data-jpa (C042)',
      api: 'GenericRepository.findAllActive(spec, p)',
      role: '정의/이력 페이지 조회',
      snippet: 'definitionRepo.findAllActive(spec, pageable)',
    },
    {
      module: 'eraf-data-jpa (C041)',
      api: 'SpecificationBuilder.eq/like',
      role: '동적 검색 + ORDER BY 인젝션 차단',
      snippet: 'SpecificationBuilder.of(ReportDefinition.class).eq("dataSource", ds)...',
    },
    {
      module: 'eraf-security (C030)',
      api: '@HasPermission("REPORT_GENERATE")',
      role: '메서드 단위 권한 (이중 방어)',
      snippet: '@HasPermission("REPORT_GENERATE") public ... generate(...)',
    },
    {
      module: 'eraf-security (C029)',
      api: 'SecurityChainCustomizer + PathPolicy',
      role: '/report-builder/** 정책',
      snippet: 'PathPolicy.hasPermission("/api/eraf/report-builder/**", "REPORT_READ")',
    },
    {
      module: 'eraf-swagger (C037)',
      api: '@ApiSuccessResponse(responses=ReportDefinitionResponse.class)',
      role: 'OpenAPI 응답 스키마',
      snippet: '(annotation 1줄)',
    },
  ];

  return (
    <div
      style={{
        marginTop: 'var(--spacing-6)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        background: 'var(--color-surface-background)',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-family-sans)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          textAlign: 'left',
        }}
      >
        <span>▾ {t('reportBuilder.commonUsage.title')}</span>
        <span style={{ fontSize: 'var(--font-size-xs)' }}>
          {t('reportBuilder.commonUsage.description')}
        </span>
      </button>

      {expanded && (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-xs)',
            }}
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {[
                  t('reportBuilder.commonUsage.col.module'),
                  t('reportBuilder.commonUsage.col.api'),
                  t('reportBuilder.commonUsage.col.role'),
                  t('reportBuilder.commonUsage.col.snippet'),
                ].map((header) => (
                  <th
                    key={header}
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      textAlign: 'left',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usages.map((row, i) => (
                <tr
                  key={i}
                  style={{
                    background:
                      i % 2 === 0
                        ? 'var(--color-surface-background)'
                        : 'var(--color-surface-muted)',
                  }}
                >
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontWeight: 600,
                      color: 'var(--color-brand-primary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.module}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-primary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.api}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-secondary)',
                      minWidth: 200,
                    }}
                  >
                    {row.role}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-muted)',
                      fontSize: 'var(--font-size-xs)',
                      maxWidth: 320,
                      overflowWrap: 'break-word',
                    }}
                  >
                    {row.snippet}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
